import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { SOCIALS, type HeroSlide } from '../data/site';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { Icon } from './Icon';

type Props = {
  slides: readonly HeroSlide[];
  autoplayMs?: number;
};

type DragStart = {
  pointerId: number;
  x: number;
};

const DEFAULT_AUTOPLAY_MS = 5000;
const SWIPE_THRESHOLD = 52;
const INTERACTIVE_SELECTOR = 'button, a, input, textarea, select, [role="button"]';

function normalizeIndex(value: number, total: number): number {
  if (total <= 0) return 0;
  return ((value % total) + total) % total;
}

export function HomeHeroCarousel({ slides, autoplayMs = DEFAULT_AUTOPLAY_MS }: Props) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const dragStart = useRef<DragStart | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const total = slides.length;

  const goTo = useCallback(
    (nextIndex: number) => setIndex(normalizeIndex(nextIndex, total)),
    [total],
  );

  const goBy = useCallback(
    (delta: number) => setIndex((current) => normalizeIndex(current + delta, total)),
    [total],
  );

  useEffect(() => {
    if (index >= total && total > 0) setIndex(0);
  }, [index, total]);

  useEffect(() => {
    if (paused || reducedMotion || total <= 1) return;
    const timer = window.setTimeout(() => goBy(1), autoplayMs);
    return () => window.clearTimeout(timer);
  }, [autoplayMs, goBy, index, paused, reducedMotion, total]);

  useEffect(() => {
    const onVisibilityChange = () => setPaused(document.hidden);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const handlePointerDown = (event: PointerEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest(INTERACTIVE_SELECTOR)) return;

    dragStart.current = { pointerId: event.pointerId, x: event.clientX };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const finishPointerGesture = (event: PointerEvent<HTMLElement>) => {
    const start = dragStart.current;
    dragStart.current = null;
    if (!start || start.pointerId !== event.pointerId) return;

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }

    const distance = event.clientX - start.x;
    if (Math.abs(distance) < SWIPE_THRESHOLD) return;
    goBy(distance > 0 ? -1 : 1);
  };

  if (!total) return null;

  return (
    <section
      className="home-hero"
      aria-roledescription="carrusel"
      aria-label="Presentación principal"
      tabIndex={0}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onPointerDown={handlePointerDown}
      onPointerUp={finishPointerGesture}
      onPointerCancel={() => { dragStart.current = null; }}
      onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goBy(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          goBy(1);
        }
      }}
    >
      <div
        className="home-hero__track"
        style={{ transform: `translate3d(-${index * 100}%, 0, 0)` }}
        aria-live="polite"
      >
        {slides.map((slide, slideIndex) => {
          const style = {
            '--hero-slide-image': `url(${slide.image})`,
            '--hero-slide-position': slide.objectPosition ?? 'center',
            '--hero-slide-position-mobile':
              slide.mobilePosition ?? slide.objectPosition ?? 'center',
          } as CSSProperties;
          const active = slideIndex === index;

          return (
            <article
              className={`home-hero__slide${active ? ' is-active' : ''}`}
              style={style}
              aria-hidden={!active}
              key={slide.id}
            >
              <div className="site-container home-hero__inner">
                <div className="home-hero__copy">
                  {slide.eyebrow ? <p className="home-hero__eyebrow">{slide.eyebrow}</p> : null}
                  {slide.title ? (
                    <h2 className="home-hero__title">
                      {slide.title.split('\n').map((line, index) => (
                        <span key={`${slide.id}-title-${index}`}>
                          {line}
                        </span>
                      ))}
                    </h2>
                  ) : null}
                  <p className="home-hero__subtitle">{slide.subtitle}</p>
                  {slide.showSocials ? (
                    <div className="home-hero__socials">
                      <span>{slide.accent}</span>
                      <a href={SOCIALS.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                        <Icon name="facebook" size={34} />
                      </a>
                      <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                        <Icon name="instagram" size={34} />
                      </a>
                      <a href={SOCIALS.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                        <Icon name="tiktok" size={34} />
                      </a>
                    </div>
                  ) : slide.accent ? (
                    <p className="home-hero__accent">{slide.accent}</p>
                  ) : null}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {total > 1 ? (
        <>
          <button
            className="home-hero__arrow home-hero__arrow--prev"
            type="button"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => goBy(-1)}
            aria-label="Diapositiva anterior"
          >
            <Icon name="arrowLeft" size={28} />
          </button>
          <button
            className="home-hero__arrow home-hero__arrow--next"
            type="button"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => goBy(1)}
            aria-label="Diapositiva siguiente"
          >
            <Icon name="arrowRight" size={28} />
          </button>

          <div className="home-hero__dots" aria-label="Seleccionar diapositiva">
            {slides.map((slide, slideIndex) => (
              <button
                key={slide.id}
                type="button"
                className={slideIndex === index ? 'is-active' : ''}
                aria-current={slideIndex === index ? 'true' : undefined}
                aria-label={`Ver diapositiva ${slideIndex + 1}`}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={() => goTo(slideIndex)}
              />
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
